/**
 * `loadRespack` (W1-08 formats-legacy-respack; contract formats/respack/types.ts; skin-and-assets.md S§2.1,
 * DESIGN.md §7.5): validates the files of a Phira resource pack exactly like Phira's importer and returns the
 * pack (null when Phira would refuse it):
 *
 *  - `info.yml` is read from the ROOT only (Phira does not strip a root folder for respacks; a pack zipped inside
 *    a folder is reported with that folder's name);
 *  - the nine images `click`, `hold`, `flick`, `drag`, their `_mh` variants and `hit_fx` must exist as `<name>.png`
 *    and decode (validate.ts sniffs the content); the hold atlas must fit hold.png / hold_mh.png;
 *  - sounds `click`, `drag`, `flick`, `ending`: the first existing of `.ogg`, `.wav`, `.mp3` must decode; missing
 *    ones fall back to Phira's built-in sounds;
 *  - warnings: `hold.*` sounds (never used), a hit-effect atlas that the grid does not divide evenly, an ARGB colour
 *    with alpha 00 (drawn opaque), and files Phira ignores.
 */

import type { MessageRef } from '../../core/util/types';
import {
  RESPACK_IMAGES,
  RESPACK_SOUNDS,
  type LoadRespackFn,
  type RespackFiles,
  type RespackImageName,
  type RespackSoundName,
  type ResPackInfo,
} from './types';
import { parseRespackInfo, sniffImage, sniffSound, verifyRespackInfo, type ImageHeader } from './validate';

/** Sound extensions in Phira's lookup order. */
export const RESPACK_SOUND_EXTS = ['ogg', 'wav', 'mp3'] as const;

/** Image headers of a pack's nine images (null entries are missing or not decodable). */
export function respackImageHeaders(
  files: ReadonlyMap<string, Uint8Array>,
): Readonly<Record<RespackImageName, ImageHeader | null>> {
  const out = {} as Record<RespackImageName, ImageHeader | null>;
  for (const name of RESPACK_IMAGES) {
    const bytes = files.get(`${name}.png`);
    out[name] = bytes === undefined ? null : sniffImage(bytes);
  }
  return out;
}

export const loadRespack: LoadRespackFn = (files) => {
  const errors: MessageRef[] = [];
  const warnings: MessageRef[] = [];
  const used = new Set<string>();

  // info.yml (root only).
  let info: ResPackInfo | null = null;
  const infoBytes = files.get('info.yml');
  if (infoBytes === undefined) {
    errors.push({ key: 'respack.error.missingInfo' });
    const nested = [...files.keys()].find((p) => /^[^/]+\/info\.yml$/.test(p));
    if (nested !== undefined)
      errors.push({ key: 'respack.error.infoInFolder', params: { folder: nested.split('/')[0] ?? '' } });
  } else {
    used.add('info.yml');
    const parsed = parseRespackInfo(infoBytes);
    errors.push(...parsed.errors);
    warnings.push(...parsed.warnings);
    info = parsed.info;
    if (info !== null) errors.push(...verifyRespackInfo(info));
  }

  // Images.
  const images = {} as Record<RespackImageName, Uint8Array>;
  const headers = {} as Record<RespackImageName, ImageHeader | null>;
  let imagesOk = true;
  for (const name of RESPACK_IMAGES) {
    const file = `${name}.png`;
    const bytes = files.get(file);
    headers[name] = null;
    if (bytes === undefined) {
      errors.push({ key: 'respack.error.missingImage', params: { file } });
      imagesOk = false;
      continue;
    }
    used.add(file);
    const header = sniffImage(bytes);
    if (header === null) {
      errors.push({ key: 'respack.error.imageUndecodable', params: { file } });
      imagesOk = false;
      continue;
    }
    images[name] = bytes;
    headers[name] = header;
  }
  if (info !== null) {
    const checks: ReadonlyArray<readonly [RespackImageName, readonly [number, number]]> = [
      ['hold', info.holdAtlas],
      ['hold_mh', info.holdAtlasMH],
    ];
    for (const [name, [tail, head]] of checks) {
      const h = headers[name];
      if (h !== null && !(tail + head < h.height)) {
        errors.push({ key: 'respack.error.holdAtlas', params: { file: `${name}.png`, tail, head, height: h.height } });
      }
    }
    const fx = headers.hit_fx;
    const [cols, rows] = info.hitFx;
    if (fx !== null && cols > 0 && rows > 0 && (fx.width % cols !== 0 || fx.height % rows !== 0)) {
      warnings.push({ key: 'respack.warning.hitFxUneven', params: { cols, rows, width: fx.width, height: fx.height } });
    }
  }

  // Sounds: the first existing extension decides.
  const sounds: Partial<Record<RespackSoundName, { readonly bytes: Uint8Array; readonly ext: 'ogg' | 'wav' | 'mp3' }>> =
    {};
  for (const name of RESPACK_SOUNDS) {
    for (const ext of RESPACK_SOUND_EXTS) {
      const file = `${name}.${ext}`;
      const bytes = files.get(file);
      if (bytes === undefined) continue;
      used.add(file);
      if (sniffSound(bytes) === null) errors.push({ key: 'respack.error.soundUndecodable', params: { file } });
      else sounds[name] = { bytes, ext };
      break;
    }
  }
  for (const ext of RESPACK_SOUND_EXTS) {
    const file = `hold.${ext}`;
    if (files.has(file)) {
      used.add(file);
      warnings.push({ key: 'respack.warning.holdSoundIgnored', params: { file } });
    }
  }
  const unused = [...files.keys()].filter((p) => !used.has(p) && !p.endsWith('/'));
  if (unused.length > 0 && infoBytes !== undefined) {
    warnings.push({ key: 'respack.warning.unusedFiles', params: { count: unused.length, first: unused[0] ?? '' } });
  }

  const pack: RespackFiles | null = errors.length === 0 && info !== null && imagesOk ? { info, images, sounds } : null;
  return { pack, validation: { errors, warnings } };
};
